import { Fragment, useContext } from "react";
import {
	Icon,
	Title,
	Image,
	Link,
	MessageCountBadge,
} from "@wdpr/groot-components";
import Profile from "components/header/profile/Profile";
import Schedule from "components/header/navigation/section/schedule/Schedule";
import MoreParkHours from "components/header/navigation/section/schedule/MoreParkHours";
import { Context } from "Context";
import { validateItemAffiliations } from "utils/affiliations";
import appConfig from "config.json";
import Button from "components/shared/Button";

const { targetOneId } = appConfig;
const mainClass = "syndicated-link-groups";
const mainListClass = `${mainClass}__list`;
const mainListItemClass = `${mainListClass}__item`;
const mainLinkClass = `${mainClass}__link`;
const mainTitleClass = `${mainClass}__title`;
const moreParkHoursId = "moreParkHours";
const authenticationOptionsId = "authenticationOptions";
const lpUpliftChatId = "availableToChatNow";
const notificationsBadgeIds = [
	"DVC_GlobalNav_Header_MyDVC_Notifications",
	"DVC_Header_Mydvcmembership_Notifications",
	"DVC_GlobalNav_Mobile_Header_MyDVC_Notifications",
];
const defaultAffiliationId = "default";
// const standardAffiliationId = "STD_GST";

const section = ({ children }) => <section>{children}</section>;

const LinkGroup = ({
	id,
	text,
	webLinkList,
	descriptions,
	modules,
	profile,
	isMobileView,
	link,
	secondary = false,
	isDecorative = false,
	duplicateSectionLinkOnMobile = false,
	showInMobile = false,
	reduceLayout = false,
	handleFullClose,
}) => {
	const {
		profileData: { user = null } = {},
		headerData: { chrome },
		profileData: { affiliations },
		config: { brand },
		chat: { chatEnvironment, launchChat },
		cdnAssetsUrl,
	} = useContext(Context);

	const titleClass = `${mainTitleClass}${
		id ? ` ${mainTitleClass}--${id}` : ""
	}`;

	const renderDescription = (
		{ icon, link, title, description, image, componentName },
		isPromo = false,
		role = null
	) => {
		return (
			<Fragment>
				{!isMobileView && (
					<Image
						image={image}
						name={id}
						isPromo={isPromo}
						title={title}
						componentName={componentName}
						role={role}
					/>
				)}
				{icon && <Icon icon={icon} cdnUrl={cdnAssetsUrl} />}
				<Title title={title || link?.title} subTitle={description} name={id} />
			</Fragment>
		);
	};

	const renderLinkGroup = (
		descriptions,
		isDescription = false,
		isPromo = false,
		isWebLinkList = false,
		linkGroupId = "",
		ariaLabelledById = null
	) => {
		const newAffiliations = chrome?.newAffiliations === "true";
		const hasParkHoursIcon = descriptions.find(
			(description) =>
				(description.id === moreParkHoursId || description.parkHour) &&
				description.icon
		);
		const descriptionsIteratorLimit = descriptions.length - 1;
		let schedulesCounter = 0;
		// TODO: Remove this logic once all brands have the new affiliations content
		let affiliationsToUse = [defaultAffiliationId];
		if (!newAffiliations && isPromo) {
			if (
				affiliations.length &&
				descriptions.filter(({ id }) => affiliations.includes(id)).length
			) {
				affiliationsToUse = [...affiliations];
			}
		}

		let groupClass = mainListClass;
		if (id) groupClass += ` ${mainListClass}--${id}`;
		if (isPromo && isDescription) groupClass += ` ${mainListClass}--promo`;
		if (linkGroupId) groupClass += ` ${mainListClass}--${linkGroupId}`;
		if (isWebLinkList) groupClass += ` ${mainListClass}--link-list`;

		const linkContent = (link, description) => (
			<Fragment>
				{isDescription ? (
					renderDescription(description, isPromo, "link")
				) : (
					<Title title={link.title} noWrap subTitle={link.subTitle} name={id} />
				)}
				{isPromo && <Icon icon="next" brand={brand} cdnUrl={cdnAssetsUrl} />}
			</Fragment>
		);
		const sectionLink = descriptions.find((description) => {
			return description.id && description.id.includes("Link");
		});
		const renderDescriptions = descriptions.map((description, index) => {
			if (isPromo) {
				if (newAffiliations) {
					if (!validateItemAffiliations(description, affiliations)) {
						return null;
					}
				} else if (!affiliationsToUse.includes(description.id)) {
					return null;
				}
			}

			if (description.id === authenticationOptionsId) {
				groupClass += ` ${mainListClass}--${authenticationOptionsId}`;
			}

			let listItemClass = mainListItemClass;
			if (description.id)
				listItemClass += ` ${mainListItemClass}--${description.id}`;
			if (isDecorative) listItemClass += ` ${mainListItemClass}--decorative`;
			if (reduceLayout && index !== 0)
				listItemClass += ` ${mainListItemClass}--reduceLayout`;

			if (description.image)
				listItemClass += ` ${mainListItemClass}--with-image`;

			const link = isDescription ? description.link : description;

			let linkClass = mainLinkClass;
			if (link) {
				if (id) linkClass += ` ${mainLinkClass}--${id}`;
				if (brand) linkClass += ` ${mainLinkClass}--${brand}`;
				if (
					description.id === moreParkHoursId ||
					(isDescription &&
						!description.title &&
						!description.image &&
						!description.icon)
				)
					linkClass += ` ${mainLinkClass}--empty`;
				if (
					["entertainmentsSchedule", "parkCalendar"].includes(description.id) &&
					hasParkHoursIcon
				)
					linkClass += ` ${mainLinkClass}--with-icons`;
				if (secondary && index === descriptionsIteratorLimit) {
					linkClass += ` ${mainLinkClass}--last-item`;
				}
			}

			if (description.id === moreParkHoursId) {
				return (
					<MoreParkHours
						description={description}
						linkClass={linkClass}
						listItemClass={listItemClass}
						name={id}
						titleClass={titleClass}
						key={index}
					/>
				);
			}

			if (description.parkHour) {
				schedulesCounter++;
				return (
					<Schedule
						description={description}
						link={link}
						listItemClass={listItemClass}
						linkClass={linkClass}
						key={index}
					/>
				);
			}

			if (description.id === lpUpliftChatId) {
				return renderChatInteractionLink(
					index,
					listItemClass,
					link,
					description,
					linkClass,
					linkContent
				);
			}
			if (description.displayInlineImage) {
				const { image, subtitle, link, title } = description;
				return (
					<li
						className={`${mainListItemClass} ${mainListItemClass}--inline-image`}
						key={index}
					>
						<Title title={title} name={id} />
						<Image image={image} />
						<Link {...link}>
							<Title subTitle={subtitle} name={description.id} />
						</Link>
					</li>
				);
			}

			return (
				<li key={index} className={listItemClass}>
					{link || showInMobile ? (
						link?.target === targetOneId ? (
							<Profile
								oneId
								target={link.target}
								renderComponent={(handleClick) => (
									<Link
										{...link}
										handleClick={handleClick}
										className={linkClass}
									>
										{linkContent(link, description)}
									</Link>
								)}
							/>
						) : index === 0 && showInMobile ? (
							isMobileView && reduceLayout ? (
								<Link {...sectionLink.link} className={`${linkClass}`}>
									{linkContent(sectionLink.link, description)}
								</Link>
							) : (
								renderDescription(description)
							)
						) : (
							<Link {...link} className={linkClass}>
								{linkContent(link, description)}
								{(user?.membership || user?.cookieBasedNotifications) &&
									notificationsBadgeIds?.includes(
										description.analyticsLinkId
									) && (
										<MessageCountBadge
											showInMobile
											counter={user?.messages?.itemCount}
											action={() => null}
										/>
									)}
							</Link>
						)
					) : (
						renderDescription(description)
					)}
					{!secondary && description.id === authenticationOptionsId && (
						<Profile userStatusLinks />
					)}
				</li>
			);
		});
		if (schedulesCounter > 3) groupClass += ` ${mainListClass}-—column-layout`;

		return (
			<ul className={groupClass} role="none">
				{renderDescriptions}
			</ul>
		);
	};

	const renderTitleAsLink = () => {
		if (duplicateSectionLinkOnMobile && isMobileView) {
			const webLinkList = [
				{ analyticsLinkId: "", href: link.href, title: link.title },
			];
			return (
				webLinkList &&
				renderLinkGroup(webLinkList, false, false, false, id, null)
			);
		}
	};

	const renderModules = (modules = []) => {
		return modules?.map(
			({ webLinkList, title, descriptions, isPromo = false, id }, index) => {
				const ariaLabelledById = id ? `navigation-${id}` : null;
				const ModuleWrapper = isPromo || !title ? Fragment : section;
				return (
					<ModuleWrapper key={index}>
						{title && (
							<h2
								id={ariaLabelledById}
								className={`${titleClass}${
									id ? ` ${mainTitleClass}--${id}` : ""
								}`}
								dangerouslySetInnerHTML={{ __html: title }}
								role="menuitem"
							/>
						)}
						{webLinkList &&
							renderLinkGroup(
								webLinkList,
								false,
								false,
								false,
								id,
								ariaLabelledById
							)}
						{descriptions && renderLinkGroup(descriptions, true, isPromo)}
						{id === authenticationOptionsId && (
							<Profile userStatusLinks detailed />
						)}
					</ModuleWrapper>
				);
			}
		);
	};

	const renderChatInteractionLink = (
		index,
		listItemClass,
		link,
		description,
		linkClass,
		linkContent
	) => {
		if (chatEnvironment) {
			const handleChatClick = async () => {
				try {
					await launchChat(handleFullClose);
				} catch (error) {
					console.error(error);
				}
			};
			return launchChat ? (
				<li key={index} className={listItemClass}>
					<Button
						analyticsLinkId={link.analyticsLinkId}
						onClick={handleChatClick}
						customClasses={linkClass}
					>
						{linkContent(link, description)}
					</Button>
				</li>
			) : null;
		} else {
			return (
				<li
					className={`${mainListItemClass} ${mainListItemClass}--chat-nav-link`}
					key={index}
				>
					<div
						id="lp-uplift-chat-nav-link"
						className={`${mainListItemClass}--chat-nav-link--desktop`}
					></div>
					<div
						id="lp-uplift-chat-nav-link-mobile"
						className={`${mainListItemClass}--chat-nav-link--mobile`}
					></div>
				</li>
			);
		}
	};

	return (
		<div className={`${mainClass} ${mainClass}--${brand}`}>
			{profile && !isMobileView && <Profile card />}
			{text && (
				<div
					className={`${mainClass}__text ${
						id ? `${mainClass}__text--${id}` : ""
					}`}
					dangerouslySetInnerHTML={{ __html: text }}
				/>
			)}
			{webLinkList && renderLinkGroup(webLinkList, false, false, true)}
			{renderTitleAsLink()}
			{modules && renderModules(modules)}
			{descriptions && renderLinkGroup(descriptions, true)}
		</div>
	);
};

export default LinkGroup;
